import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/extrato')({ component: AdminExtrato });

type Entry = {
  date: string;
  type: string;
  direction: 'C' | 'D';
  amount: number;
  title: string;
  description: string;
};

const money = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (d: string) => {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

// Default: últimos 30 dias (dentro do limite de 90 do Inter)
function defaultRange() {
  const to = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 10);
  return { from, to };
}

function AdminExtrato() {
  const [range, setRange] = useState(defaultRange);
  const [draft, setDraft] = useState(range);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['admin-inter-statement', range.from, range.to],
    queryFn: () =>
      api
        .get(`admin/inter/statement?from=${range.from}&to=${range.to}`)
        .json<{ entries: Entry[]; error?: string }>(),
    staleTime: 60_000,
  });

  const entries = data?.entries ?? [];
  const totalIn = entries.filter((e) => e.direction === 'C').reduce((s, e) => s + e.amount, 0);
  const totalOut = entries.filter((e) => e.direction === 'D').reduce((s, e) => s + e.amount, 0);

  return (
    <>
      <AdminPageHeader
        title="Extrato Inter"
        subtitle="Movimentação da conta bancária da aplicação"
      />

      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 flex items-end gap-3 flex-wrap">
        <label className="text-xs font-medium text-slate-600">
          De
          <input
            type="date"
            value={draft.from}
            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
            className="mt-1 block bg-white border border-slate-200 rounded-md h-9 px-3 text-sm"
          />
        </label>
        <label className="text-xs font-medium text-slate-600">
          Até
          <input
            type="date"
            value={draft.to}
            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
            className="mt-1 block bg-white border border-slate-200 rounded-md h-9 px-3 text-sm"
          />
        </label>
        <Button
          onClick={() => setRange(draft)}
          disabled={isFetching}
          className="bg-teal-500 hover:bg-teal-600 text-white h-9"
        >
          {isFetching ? 'Carregando...' : 'Filtrar'}
        </Button>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching} className="h-9">
          ↻ Atualizar
        </Button>
        <span className="text-[11px] text-slate-400 ml-auto self-center">
          Período máximo: 90 dias
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-teal-700">Entradas</p>
          <p className="text-lg font-bold text-teal-800">{money(totalIn)}</p>
        </div>
        <div className="bg-coral-50 border border-coral-200 rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-coral-700">Saídas</p>
          <p className="text-lg font-bold text-coral-800">{money(totalOut)}</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Saldo do período</p>
          <p className={cn('text-lg font-bold', totalIn - totalOut >= 0 ? 'text-teal-800' : 'text-coral-800')}>
            {money(totalIn - totalOut)}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {data?.error ? (
          <div className="p-8 text-center text-sm text-red-600">⚠ {data.error}</div>
        ) : isFetching ? (
          <div className="p-12 text-center text-sm text-slate-400">Carregando extrato...</div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-400">
            Nenhuma movimentação no período.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Data</th>
                  <th className="text-left px-4 py-3 font-semibold">Tipo</th>
                  <th className="text-left px-4 py-3 font-semibold">Descrição</th>
                  <th className="text-right px-4 py-3 font-semibold">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map((e, i) => (
                  <tr key={i} className="hover:bg-slate-50/60">
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(e.date)}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-[10px] px-2 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200 whitespace-nowrap">
                        {e.type}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">{e.title || e.description || '—'}</td>
                    <td
                      className={cn(
                        'px-4 py-2.5 text-right tabular-nums font-semibold whitespace-nowrap',
                        e.direction === 'C' ? 'text-teal-700' : 'text-coral-600',
                      )}
                    >
                      {e.direction === 'C' ? '+' : '−'} {money(e.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
